import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons';
import { Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router';

import { DRAFT_ORDERS, PATIENTS } from '@constants/routes';

import './PatientOrderReanalysisBanner.scss';

export interface PatientOrderReanalysisBannerProps {
  /** The original requisition to reanalyze. */
  requisitionId: string;
  /** Product IDs eligible for reanalysis; forwarded so the order form can seed `selected_product_ids`. */
  reanalysisProductIds: string[];
  /** Campaign code for Natera (and other) campaign rerequisition reanalysis flows. */
  reanalysisCampaignCode?: string;
  /** Active draft for this reanalysis, when the user has saved progress. */
  activeReanalysisDraftId?: string;
}

export default function PatientOrderReanalysisBanner({
  requisitionId,
  reanalysisProductIds,
  reanalysisCampaignCode,
  activeReanalysisDraftId,
}: PatientOrderReanalysisBannerProps) {
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isResume = Boolean(activeReanalysisDraftId);

  const onPrimaryAction = () => {
    if (activeReanalysisDraftId && patientId) {
      navigate(`${DRAFT_ORDERS}/${activeReanalysisDraftId}`, {
        state: { prevPath: `${PATIENTS}/${patientId}` },
      });
      return;
    }

    const search = new URLSearchParams();
    if (reanalysisCampaignCode) {
      search.set('campaign', reanalysisCampaignCode);
    }
    reanalysisProductIds.forEach(id => search.append('product_ids', id));
    const qs = search.toString();
    navigate(`/patients/${patientId}/order-reanalysis/${requisitionId}${qs ? `?${qs}` : ''}`);
  };

  return (
    <div className='patient-detail-order-banner' role='region' aria-label='Reanalysis Available'>
      <div className='patient-detail-order-banner__lead'>
        <div className='patient-detail-order-banner__icon' aria-hidden>
          <FontAwesomeIcon icon={faArrowsRotate as IconDefinition} />
        </div>

        <div className='patient-detail-order-banner__text'>
          <p className='patient-detail-order-banner__title'>Reanalysis Available</p>

          <p className='patient-detail-order-banner__description'>
            A reanalysis of the test(s) originally processed can be ordered once at no additional cost.
          </p>
        </div>
      </div>

      <div className='patient-detail-order-banner__action'>
        <Button
          className='patient-detail-order-banner__cta'
          onClick={onPrimaryAction}
          size='sm'
          variant='primary'
          aria-label={isResume ? 'Resume reanalysis order' : 'Get started with reanalysis order'}
        >
          {isResume ? 'Resume Order' : 'Get Started'}
        </Button>
      </div>
    </div>
  );
}
